/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2025-01-21 11:06:38                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-21 11:18:34                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Helpers
  * GmapsLoaded - load Google Maps script before rendering children
*/

/* Imports */
import { api } from "../api/api";
import { useCallback, useEffect, useState } from "react";
import { Typography } from "@material-tailwind/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { t } from "i18next";
/***/

/* Helpers */
/**
 * getGmapsApiKey - Retourne la clé d'API Google Maps
 * Sert la valeur mise en cache si elle existe, sinon interroge le backend.
 * Lève une erreur si le backend est injoignable ou ne renvoie pas de clé :
 * l'appelant affiche alors un écran d'erreur au lieu de relancer la requête.
 */
const getGmapsApiKey = async (): Promise<string> => {
  const cached = localStorage.getItem("gmapsApiKey");
  if (cached) return cached;

  const ret = await api.get("admin/webappconfig/gmapsapikey") as { response?: string };
  if (!ret?.response) throw new Error("Empty Google Maps API key");

  localStorage.setItem("gmapsApiKey", ret.response);
  return ret.response;
};
/***/

/**
* GmapsLoaded - Charge le script Google Maps avant d'afficher ses enfants
* @param children - Contenu affiché une fois le script chargé
*/
export default function GmapsLoaded({
  children
}: {
  children: any
}) {
  const [gmapsInit, setGmapsInit] = useState(false);
  const [realmState, setRealmState] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const importGmapsScript = useCallback(async () => {
    const realm = localStorage.getItem("realm");

    if ((gmapsInit === true && realm === realmState)) return;
    if (failed) return; // Attend une nouvelle tentative explicite de l'utilisateur

    let apiKey: string;

    try {
      apiKey = await getGmapsApiKey();
    } catch (err) {
      console.error("Unable to retrieve Google Maps API key", err);
      setFailed(true);
      return;
    }

    const scriptTag = document.createElement("script");
    scriptTag.setAttribute("id", "gmapsapiscript");
    scriptTag.src = "https://maps.googleapis.com/maps/api/js?key="+apiKey+"&libraries=places&loading=async";

    const old = document.getElementById("gmapsapiscript");
    if (old)
      document.body.replaceChild(scriptTag, old);
    else
      document.body.appendChild(scriptTag);

    setTimeout(() => {
      setRealmState(realm);
      setGmapsInit(true);
    }, 200);
  }, [gmapsInit, realmState, failed]);


  useEffect(() => {
    importGmapsScript();
  }, [importGmapsScript]);

  if (failed) {
    return (
      <div className="h-screen flex items-center justify-center flex-col gap-6 bg-gray-50 px-4">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <div className="w-20 h-20 rounded-full bg-orange-100 flex items-center justify-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-orange-500" />
          </div>

          <Typography variant="h3" className="text-gray-800">
            {t("error:backendUnreachable:title")}
          </Typography>

          <Typography className="text-gray-600">
            {t("error:backendUnreachable:message")}
          </Typography>

          <button
            onClick={() => setFailed(false)}
            className="mt-4 px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            {t("error:backendUnreachable:retry")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {gmapsInit ?
        children
      :
        null
      }
    </>
  );
}
/***/
